import Store from '@ember-data/store';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'donor-elf-web/models/campaign';
import CampaignGoalModel from 'donor-elf-web/models/campaign-goal';
import AddSomethingModal, {
  type CampaignGoalSavedArgs,
  type CampaignSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import Settings from 'donor-elf-web/services/settings';
import type { DashboardCampaignChangePinArgs } from './campaign';

export class DashboardCampaignItem {
  constructor(campaign: CampaignModel) {
    this.campaign = campaign;
    this.campaignId = campaign.id;
  }

  campaign: CampaignModel;
  campaignId: string;
  @tracked expanded = false;
  @tracked campaignGoal?: CampaignGoalModel;
  @tracked childrenDashboardCampaignItems: DashboardCampaignItem[] = [];
  @tracked pinned: boolean = false;
  @tracked pinOptions: DashboardCampaignPinOptions = {};
}

interface DashboardCampaignPinOptions {
  showPin?: boolean;
  showUnpin?: boolean;
  showMoveUp?: boolean;
  showMoveDown?: boolean;
}

export default class DashboardCampaignsIndexComponent extends Component {
  constructor(owner: any, args: any) {
    super(owner, args);
    this.initializeAllItems();
    this.campaignSavedSubsciptionToken =
      this.addSomethingModal.subscribeToCampaignSaved(this.handleCampaignSaved);
    this.campaignGoalSavedSubsciptionToken =
      this.addSomethingModal.subscribeToCampaignGoalSaved(
        this.handleCampaignGoalSaved,
      );
    this.parentCampaignChangedSubsciptionToken =
      this.addSomethingModal.subscribeToParentCampaignChanged(
        this.handleParentCampaignChanged,
      );
    this.deleteCampaignSubscriptionToken =
      this.addSomethingModal.subscribeToCampaignDeleted(
        this.handleCampaignDeleted,
      );
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare campaignService: CampaignService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked pinnedItems: DashboardCampaignItem[] = [];
  @tracked items: DashboardCampaignItem[] = [];
  campaignSavedSubsciptionToken?: string;
  campaignGoalSavedSubsciptionToken?: string;
  parentCampaignChangedSubsciptionToken?: string;
  deleteCampaignSubscriptionToken?: string;

  initializeAllItems() {
    this.initializePinnedItems();
    let items: DashboardCampaignItem[] = [];
    for (let campaign of this.campaignService.sortedCampaigns.filter(
      (x) => x.parentCampaign == null,
    )) {
      let item = new DashboardCampaignItem(campaign);
      item.pinOptions = { showPin: true };
      this.addCampaignGoalAndChildrenDashboardCampaignItems(item);
      items.push(item);
    }
    this.items = items;
  }

  initializePinnedItems() {
    let pinnedItems: DashboardCampaignItem[] = [];
    let pinnedCampaignIds =
      this.settings.userFundSettingsByType['pinned_campaigns']?.vals
        ?.campaign_ids || [];
    for (let [index, campaignId] of pinnedCampaignIds.entries()) {
      let campaign = this.campaignService.campaignsById[campaignId];
      if (campaign) {
        let item = new DashboardCampaignItem(campaign);
        item.pinned = true;
        item.pinOptions = {
          showUnpin: true,
          showMoveUp: index > 0,
          showMoveDown: index + 1 < pinnedCampaignIds.length,
        };
        this.addCampaignGoalAndChildrenDashboardCampaignItems(item);
        item.expanded = true;
        pinnedItems.push(item);
      }
    }
    this.pinnedItems = pinnedItems;
  }

  addCampaignGoalAndChildrenDashboardCampaignItems(
    item: DashboardCampaignItem,
  ) {
    item.campaignGoal =
      this.campaignService.activeCampaignGoalsByCampaignId[item.campaign.id];
    item.childrenDashboardCampaignItems = item.campaign.childrenCampaigns.map(
      (childCamp) => {
        let childItem = new DashboardCampaignItem(childCamp);
        childItem.pinOptions = { showPin: true };
        this.addCampaignGoalAndChildrenDashboardCampaignItems(childItem);
        return childItem;
      },
    );
  }

  handleCampaignSaved = async (_msg: string, data: CampaignSavedArgs) => {
    if (data.newCampaign) {
      if (data.campaign.parentCampaignId) {
        let parentItems =
          this.allItemsByCampaignId[data.campaign.parentCampaignId!];
        if (parentItems) {
          for (let parentItem of parentItems) {
            let item = new DashboardCampaignItem(data.campaign);
            this.addCampaignGoalAndChildrenDashboardCampaignItems(item);
            parentItem.childrenDashboardCampaignItems = [
              ...parentItem.childrenDashboardCampaignItems,
              item,
            ];
          }
        }
      } else {
        let item = new DashboardCampaignItem(data.campaign);
        this.addCampaignGoalAndChildrenDashboardCampaignItems(item);
        this.items = [...this.items, item];
      }
    }
  };

  handleCampaignGoalSaved = async (
    _msg: string,
    data: CampaignGoalSavedArgs,
  ) => {
    if (data.newCampaignGoal) {
      let items = this.allItemsByCampaignId[data.campaign.id];
      if (items) {
        for (let item of items) {
          item.campaignGoal = data.campaignGoal;
        }
      }
    }
  };

  handleParentCampaignChanged = async () => {
    // have to re-initialize all the items since the hierarchy changed
    this.initializeAllItems();
  };

  handleCampaignDeleted = async () => {
    this.initializeAllItems();
  };

  handleChangePin = async (args: DashboardCampaignChangePinArgs) => {
    let userFundSettingPinned =
      this.settings.userFundSettingsByType['pinned_campaigns'];
    if (userFundSettingPinned == null) {
      userFundSettingPinned = this.store.createRecord('user-fund-setting');
      userFundSettingPinned.settingType = 'pinned_campaigns';
      userFundSettingPinned.vals = { campaign_ids: [] };
    }
    let pinnedCampaignIds = userFundSettingPinned.vals.campaign_ids;
    let campaignId = args.campaign.id;
    if (args.pin) {
      if (!pinnedCampaignIds.includes(campaignId)) {
        pinnedCampaignIds.push(campaignId);
      }
    } else if (args.unpin) {
      pinnedCampaignIds = pinnedCampaignIds.filter((x) => x != campaignId);
    } else if (args.moveDown) {
      let index = pinnedCampaignIds.indexOf(campaignId);
      pinnedCampaignIds.splice(index, 1);
      pinnedCampaignIds.splice(index + 1, 0, campaignId);
    } else if (args.moveUp) {
      let index = pinnedCampaignIds.indexOf(campaignId);
      pinnedCampaignIds.splice(index, 1);
      pinnedCampaignIds.splice(index - 1, 0, campaignId);
    }
    userFundSettingPinned.vals.campaign_ids = pinnedCampaignIds;
    if (userFundSettingPinned.isNew as unknown) {
      await userFundSettingPinned.save();
      this.settings.onUserFundSettingCreated(userFundSettingPinned);
    } else {
      userFundSettingPinned.save();
    }

    this.initializePinnedItems();
  };

  // returning an array for each campaignId because a single campaign can be in both
  // the pinnedItems and items arrays
  get allItemsByCampaignId(): {
    [campaignId: string]: DashboardCampaignItem[];
  } {
    let itemsById: { [campaignId: string]: DashboardCampaignItem[] } = {};
    for (let item of [...this.pinnedItems, ...this.items]) {
      let itemAndDescendants = this.getItemAndDescendantsByCampaignId(item);
      for (let campaignId in itemAndDescendants) {
        let campaignItem = itemAndDescendants[campaignId];
        if (itemsById[campaignId] == null) {
          itemsById[campaignId] = [];
        }
        itemsById[campaignId].push(campaignItem);
      }
    }
    return itemsById;
  }

  getItemAndDescendantsByCampaignId(item: DashboardCampaignItem) {
    let byId: { [campaignId: string]: DashboardCampaignItem } = {};
    byId[item.campaignId] = item;
    for (let childItem of item.childrenDashboardCampaignItems) {
      byId = {
        ...byId,
        ...this.getItemAndDescendantsByCampaignId(childItem),
      };
    }
    return byId;
  }

  willDestroy() {
    if (this.campaignSavedSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.campaignSavedSubsciptionToken,
      );
    }
    if (this.campaignGoalSavedSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.campaignGoalSavedSubsciptionToken,
      );
    }
    if (this.parentCampaignChangedSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.parentCampaignChangedSubsciptionToken,
      );
    }
    if (this.deleteCampaignSubscriptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.deleteCampaignSubscriptionToken,
      );
    }
  }
}
